/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Loader from '../../component/common/Loader';
import Breadcrumb from '../../component/common/Breadcrumb';
import SearchInput from '../../component/common/Input/SearchInput';
import Table from '../../component/common/Table';
import Pagination from '../../component/common/Pagination/Pagination';
import Topbar from '../PulseSurvey/Topbar';
import ConfirmPopup from '../../component/common/modals/ConfirmPopup';
import { errorToast, successToast, isSuperAdmin } from '../../utils/helper';
import { getGameAchievements, deleteGameAchievement } from '../../api/achievementApi';
import { PER_PAGE } from '../../utils/constants';
import CreateAchievementModal from '../../component/common/modals/CreateAchievementModal';
import { TableCard } from '../../component/common/designSystem';

const getPages = (isAdmin, slug) => {
  if (isAdmin) {
    return [{ name: 'Achievements', href: '/achievements', current: true }];
  } else {
    return [{ name: 'Achievements', href: `/${slug}/achievements`, current: true }];
  }
};

const Achievement = () => {
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const { slug } = userData;
  const isAdmin = isSuperAdmin();

  const [loader, setLoader] = useState(true);
  const [achievements, setAchievements] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPerPage, setSelectedPerPage] = useState(PER_PAGE[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [achievementToDelete, setAchievementToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchAchievements();
  }, [currentPage, selectedPerPage, searchTerm]);

  const fetchAchievements = async () => {
    setLoader(true);
    try {
      const params = {
        page: currentPage,
        limit: selectedPerPage.value
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await getGameAchievements(params);
      console.log('Achievements Response:', response);

      if (response?.data) {
        // Add action component to each achievement
        const achievementsWithActions = (response.data || []).map((achievement) => ({
          ...achievement,
          actions: (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => handleEdit(achievement)}
                className="p-2 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                title="Edit Achievement"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDeleteClick(achievement)}
                className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                title="Delete Achievement"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          )
        }));
        setAchievements(achievementsWithActions);
        setTotalCount(response.meta?.totalCount || response.totalCount || 0);
      } else {
        setAchievements([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      errorToast('Failed to load achievements');
      setAchievements([]);
      setTotalCount(0);
    } finally {
      setLoader(false);
    }
  };

  const handlePerPage = (perPage) => {
    setSelectedPerPage(perPage);
    setCurrentPage(1);
  };

  const handlePagination = (page) => {
    setCurrentPage(page);
  };

  const refreshTable = () => {
    setCurrentPage(1);
    setSearchTerm('');
    fetchAchievements();
  };

  const handleCreateAchievement = () => {
    setEditingAchievement(null);
    setIsCreateModalOpen(true);
  };

  const handleEdit = (achievement) => {
    setEditingAchievement(achievement);
    setIsCreateModalOpen(true);
  };

  const handleDeleteClick = (achievement) => {
    setAchievementToDelete(achievement);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!achievementToDelete) return;

    setIsDeleting(true);
    try {
      const response = await deleteGameAchievement(achievementToDelete._id);
      if (response) {
        successToast('Achievement deleted successfully');
        setDeleteConfirmOpen(false);
        setAchievementToDelete(null);
        fetchAchievements();
      }
    } catch (error) {
      console.error('Error deleting achievement:', error);
      errorToast(error?.message || 'Failed to delete achievement');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAchievementToDelete(null);
  };

  const handleModalSuccess = () => {
    setEditingAchievement(null);
    fetchAchievements();
  };

  const handleModalClose = () => {
    setEditingAchievement(null);
  };

  const columns = [
    {
      title: 'Title',
      key: 'title',
      sortable: false,
      type: 'text',
      align: 'left'
    },
    {
      title: 'Description',
      key: 'description',
      sortable: false,
      type: 'text',
      align: 'left'
    },
    {
      title: 'Type',
      key: 'typeName',
      sortable: false,
      type: 'text',
      align: 'left'
    },
    {
      title: 'Target Value',
      key: 'targetValue',
      sortable: false,
      type: 'text',
      align: 'center'
    },
    {
      title: 'Rewards',
      key: 'rewards',
      sortable: false,
      type: 'text',
      align: 'center'
    },
    {
      title: 'Status',
      key: 'status',
      sortable: false,
      type: 'badge',
      align: 'center'
    },
    {
      title: 'Created At',
      key: 'createdAt',
      sortable: false,
      type: 'date',
      align: 'left'
    },
    {
      title: 'Actions',
      type: 'action',
      isEdit: true,
      handleEdit: handleEdit,
      isDelete: true,
      handleDelete: handleDeleteClick,
      sortable: false,
      align: 'center'
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Achievements | Shoorah Admin</title>
      </Helmet>

      {loader && <Loader />}

      <div className="ml-2">
        <div className="flex justify-between items-center py-4">
          <Topbar title={'Game Achievements'} />
        </div>

        <Breadcrumb pageList={getPages(isAdmin, slug)} className="lg:mt-8" />

        {/* Achievements Table Section */}
        <div className="mt-12">
          <div className="bg-white p-3 dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
            <div className="flex justify-between items-center mb-6 gap-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-[250px]">
                  <SearchInput
                    id="searchKey"
                    name="searchKey"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search achievements..."
                  />
                </div>
              </div>
              <button
                onClick={handleCreateAchievement}
                className="btn-primary flex-shrink-0"
              >
                Create Achievement
              </button>
            </div>

            <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: selectedPerPage?.value,
            onPageChange: handlePagination,
            onPageSizeChange: (size) =>
              handlePerPage(PER_PAGE.find((o) => o.value === size) || PER_PAGE[0]),
            pageSizeOptions: PER_PAGE.map((o) => o.value)
          }}
        >
          <Table
              columns={columns}
              data={achievements}
              name={'achievements_table'}
              bottomBorder={totalCount > selectedPerPage.value}
              loader={loader}
              refreshTable={refreshTable}
              contentType={1}
            />
        </TableCard>
          </div>

          {/* Pagination */}
          {totalCount > 0 && (
            <div className="mt-4">
              
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Achievement Modal */}
      <CreateAchievementModal
        open={isCreateModalOpen}
        setOpen={setIsCreateModalOpen}
        onSuccess={handleModalSuccess}
        onClose={handleModalClose}
        editData={editingAchievement}
      />

      {/* Delete Confirmation Popup */}
      <ConfirmPopup
        open={deleteConfirmOpen}
        setOpen={setDeleteConfirmOpen}
        setAccepted={handleDeleteConfirm}
        handleNo={handleDeleteCancel}
        message={`Are you sure you want to delete "${achievementToDelete?.title}"? This action cannot be undone.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Achievement;
